import * as $ from "jquery";
import * as _ from "lodash";

import { OrthographicExtension } from "./extensions/orthographicExtension";
import { PropertyPanelExtension } from "./extensions/property/propertyPanelExtension";
import { CustomColorExtension } from "./extensions/customColor/customColorExtension";
import { BuildOrderExtension } from "./extensions/buildOrder/buildOrderExtension";
import { ModelStructureExtension } from "./extensions/modelStructure/modelStructureExtension";
import { ExplodeExtension } from "./extensions/explodeExtension";
import { ZoomExtension } from "./extensions/zoomExtension";
import { MeasurementExtension } from "./extensions/measurement/measurementExtension";
import { ToolHiderExtension } from "./extensions/toolHiderExtension";
import { SettingsExtension } from "./extensions/settings/settingsExtension";
import { StatusExtension } from "./extensions/status/statusExtension";
import { AnalyticsExtension } from "./extensions/analytics/analyticsExtension";
import { PdmsAttrExtension } from "./extensions/pdms/pdmsAttrExtension";
import { RoleExtension } from "./extensions/role/roleExtension";
import { MeasurementDataExtension } from "./extensions/measurementData/measurementDataExtension";
import { ShareExtension } from "./extensions/share/shareExtension";
import { NewCommentExtension } from "./extensions/comment/newCommentExtension";
import { CommentViewerUtils } from "./extensions/comment/commentViewerUtils";
import { QuickFilterExtension } from "./extensions/filter/quickFilterExtension";
import { QuickFilterUtils } from "./extensions/filter/quickFilterUtils";
import { MarkupExtension } from "./extensions/markup/MarkupExtension";
import { WebExtension } from "./extensions/web/webExtension";
import { NodeExtension } from "./extensions/node/nodeExtension";
import { PositionExtension } from "./extensions/positionExtension";
import { ExternalDataExtension } from "./extensions/externalData/externalDataExtension";
import { ToolTipExtension } from "./extensions/toolTipExtension";
import { GroupSelectionExtension } from "./extensions/externalData/groupSelectionExtension";
import { LegendPropertyExtension } from "./extensions/externalData/legendPropertyExtension";
import { BalconyGlassExtension } from "./extensions/customized/balconyGlasses/balconyGlassExtension";
import { PdfNavExtension } from "./extensions/pdfNavExtension";
import { CombiModelTreeExtension } from "./extensions/combiModelTreeExtension";
import { BcfExtension } from "./extensions/bcf/bcfExtension";

import * as SmartDrawings from "./smartDrawings";
import { ModelApi } from "./api/modelApi";
import { FirstPerson } from "./tools/firstPerson";
import { IdMap } from "./common/idMap";
import { DnDFileController } from "./tools/dndFileController";
import { UiUtils } from "./common/uiUtils";
import { ViewerUtils } from "./common/viewerUtils";
import { GfxUtils } from "./common/gfxUtils";
import { SettingsUtils } from "./extensions/settings/settingsUtils";
import { Misc } from "./common/misc";
import { ShareUtils } from "./extensions/share/shareUtils";
import { initSearch, search, updateSearchBar, checkSearchReady } from "./tools/searchBar";
import { initMenu } from "./tools/tourMenu";
import { SdApi } from "./api/sdApi";
import { UnitConstants } from "./common/constants";

/**
 * Module with all the Forge viewer initialization stuff.
 */

let viewer: Autodesk.Viewing.GuiViewer3D;
let modelBaseUrl: string;
let env;
let mainModelLoaded = false;
let viewerOptions: any = { idMap: new IdMap() }; // SmartDrawings.Model;
let viewerState: SmartDrawings.ViewerState = {};
let dnd: DnDFileController;
const dndListeners: { (file: any, jsonObject: any): boolean }[] = [];
let helperGfx;
let commentViewerUtils: CommentViewerUtils;

let sdApi : SdApi;

try {
    (Autodesk.Viewing.Private as any).analytics.optOut();
} catch (e) {}

async function startLocal(viewModel: SmartDrawings.ViewModel, baseUrl: string = null,
    sandboxToken: string = null, queryParameters: string = null) {

    // Reset if another model is already loaded
    if (viewerOptions) {
        viewerOptions = { idMap: new IdMap() };
    }

    if (sandboxToken != null) {
        (Autodesk.Viewing.endpoint as any).HTTP_REQUEST_HEADERS["sandbox"] = sandboxToken;
        ModelApi.setSandboxToken(sandboxToken);
    }

    // If query parameters contain view token (vt), set the headers for it
    if (queryParameters != null && queryParameters.trim().length > 0) {
        let search = new URLSearchParams(queryParameters);
        if (search.has("vt")) {
            (Autodesk.Viewing.endpoint as any).HTTP_REQUEST_HEADERS["sd-view-token"] = search.get("vt");
            ModelApi.setViewToken(search.get("vt"));
        }
    }

    // Always use view token of the model, if defined (override possible query parameter)
    if (viewModel.viewToken != null) {
        (Autodesk.Viewing.endpoint as any).HTTP_REQUEST_HEADERS["sd-view-token"] = viewModel.viewToken;
        ModelApi.setViewToken(viewModel.viewToken);
    }

    modelBaseUrl = baseUrl;
    ModelApi.setBaseUrl(baseUrl);
    const resp = await ModelApi.getModel(viewModel.guid);
    const model: SmartDrawings.Model = resp.data;

    const mainFile = getMainFile(model);

    let unitData = initUnitData();

    const options = {
        env: "Local",
        document: mainFile,
        applyRefPoint: true,
        globalOffset: { x: 0, y: 0, z: 0 },
        applyScaling: unitData.viewerUnit
    };

    const hiddenNodes: Array<SmartDrawings.ModelNode> = [];

    env = options.env;
    viewerOptions =
    {
        ...viewerOptions,
        ...model,
        unitData,
        hiddenNodes
    };

    await initializeViewer(options, model);

    UiUtils.setNotice(model);
    attachDnd();
    await Misc.initRecaptcha(viewerOptions.recaptchaKey);
    return viewer;
}

function initializeViewer(options, model) {

    return new Promise((resolve, reject) => {
        Autodesk.Viewing.Initializer(options, () => {
            registerExtensions();

            const { extensions } = viewerOptions.tools;
            const config3d = {
                extensions: extensions != null ? extensions.map(ext => ext.name) : [],
                // Disable box selection -> ctrl + mouseclick for multiselect
                disabledExtensions: { boxSelection: true }
            };

            const viewerDiv = document.getElementById("forge-viewport");
            viewer = new Autodesk.Viewing.GuiViewer3D(viewerDiv, config3d);

            UiUtils.addLoader();
            (viewer as any).start(options.document, options, onLoadModelSuccess, onLoadModelError);
            if (_.toUpper(model.type) === "PDF") {
                viewer.loadExtension("Autodesk.DocumentBrowser");
                UiUtils.removeLoader();
                viewer.disableHighlight(true);
                viewer.disableSelection(true);
            }
            viewer.loadExtension("Autodesk.Viewing.MarkupsCore");
            resolve(true);
        });
    });
}

/**
 * viewer.loadModel() success callback.
 * Invoked after the model's SVF has been initially loaded.
 * It may trigger before any geometry has been downloaded and displayed on-screen.
 */
async function onLoadModelSuccess(model) {
    setupModelEvents(1);

    const onObjectTreeCreated = async () => {
        try {
            viewer.removeEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, onObjectTreeCreated);

            // Fetch guid to dbid mapping, if it exists.
            await initIdMap();

            // Fetch external data which can contain properties and coloring.
            await initExternalData();

            // Fetch content to show in the web extension.
            await initWebData();

            // Fetch tour data (e.g. BIP)
            await initTourData();

        } catch (e) {
            console.log("ID map not available.");
        } finally {
            // TODO: What if both parameters (zoom and search) are used
            setTimeout(searchAccordingToUrlParams, 500);
        }
    }

    if (viewer.model.isObjectTreeCreated())
        await onObjectTreeCreated();
    else
        viewer.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, onObjectTreeCreated);

    finalizeViewer(true);
}

async function startCombo(viewModel: SmartDrawings.ComboViewModel) {
    const resp = await ModelApi.getComboModel(viewModel.comboModelId, viewModel.primarySubModelId);
    const model: SmartDrawings.ComboModel = resp.data;

    const mainModel = model.models[0];

    let unitData = initUnitData();

    const mat = new THREE.Matrix4();
    const { position, rotation, scale } = mainModel;
    const options = {
        env: "Local",
        applyRefPoint: true,
        globalOffset: { x: 0, y: 0, z: 0 },
        applyScaling: unitData.viewerUnit,
        placementTransform: mat.compose(new THREE.Vector3(position.x, position.y, position.z),
            new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w), new THREE.Vector3(scale.x, scale.y, scale.z))
    };

    const hiddenNodes: Array<SmartDrawings.ModelNode> = [];

    env = options.env;
    viewerOptions =
    {
        ...viewerOptions,
        ...model,
        unitData,
        hiddenNodes
    }

    const onInitialized = () => {

        registerExtensions();

        const { extensions } = viewerOptions.tools;
        const config3d = {
            extensions: extensions != null ? extensions.map(ext => ext.name) : [],
            // Disable box selection -> ctrl + mouseclick for multiselect
            disabledExtensions: { boxSelection: true }
        };

        const viewerDiv = document.getElementById("forge-viewport");
        viewer = new Autodesk.Viewing.GuiViewer3D(viewerDiv, config3d);
        viewer.start();

        // Loaded models for SD combi model
        // Order might be different from original model list
        var loadedModelOrder: Array<SmartDrawings.SubModel> = [];

        setupModelEvents(model.models.length, loadedModelOrder);

        UiUtils.addLoader();
        viewer.loadExtension("Autodesk.Viewing.MarkupsCore");

        const tasks = [];
        model.models.forEach(md => tasks.push(
            loadManifest(viewer, md, () => loadedModelOrder.push(md)))
        );

        Promise.all(tasks).then(() => finalizeViewer(false));
    };

    Autodesk.Viewing.Initializer(options, onInitialized);

    UiUtils.setNotice(model);
    attachDnd();
    await Misc.initRecaptcha(viewerOptions.recaptchaKey);
}

function loadManifest(viewer: Autodesk.Viewing.Viewer3D, model: SmartDrawings.SubModel,
    onManifestLoaded: (model: SmartDrawings.SubModel) => void) {

    return new Promise((resolve, reject) => {
        const onDocumentLoadSuccess = (model: Autodesk.Viewing.Model) => {
            resolve(model);
        };
        const { position, rotation, scale } = model as any;
        const mat = new THREE.Matrix4();
        const options = {
            env: "Local",
            applyRefPoint: true,
            globalOffset: { x: 0, y: 0, z: 0 },
            placementTransform: mat.compose(new THREE.Vector3(position.x, position.y, position.z),
                new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w), new THREE.Vector3(scale.x, scale.y, scale.z)),
            keepCurrentModels: true,
            applyScaling: viewerOptions.unitData.viewerUnit
        };

        viewer.loadModel(`${ViewerUtils.sdUrl()}/${getMainFile(model, true)}`, options, (loadedModel) => {
            onManifestLoaded(model);
            onDocumentLoadSuccess(loadedModel);
        }, onLoadModelError);
    });
}

/**
 * viewer.loadModel() failure callback.
 * Invoked when there's an error fetching the SVF file.
 */
function onLoadModelError(viewerErrorCode) {
    console.error("onLoadModelError() - errorCode:" + viewerErrorCode);
    UiUtils.removeLoader();
}

/**
 * Get loaded model file according to its type.
 */
function getMainFile(model, combined = false): string {
    const base = modelBaseUrl != null ? modelBaseUrl + "/sd/" : "";
    let file: string;
    switch (_.toUpper(model.type)) {
        case "DWG":
            file = combined ? `${base}${model.guid}/0.svf` : `${model.guid}/primaryGraphics.f2d`;
            break;
        case "PDF":
            file = `${base}${model.guid}/0.pdf`;
            break;
        default:
            file = `${base}${model.guid}/0.svf`;
    }
    return file;
}

function initUnitData(): ViewerUtils.ViewerUnitData {
    let unitData: ViewerUtils.ViewerUnitData = {
        modelUnit: Autodesk.Viewing.Private.ModelUnits.MILLIMETER,
        viewerUnit: Autodesk.Viewing.Private.ModelUnits.METER,
        modelToViewerScale: 1,
        viewerToModelScale: 1
    };

    unitData.modelToViewerScale = ViewerUtils.getUnitScaleFactor(unitData.modelUnit, unitData.viewerUnit);
    unitData.viewerToModelScale = 1 / unitData.modelToViewerScale

    return unitData;
}

function registerExtensions() {
    OrthographicExtension.registerExtension();
    PropertyPanelExtension.registerExtension();
    CustomColorExtension.registerExtension();
    BuildOrderExtension.registerExtension();
    ModelStructureExtension.registerExtension();
    ExplodeExtension.registerExtension();
    ZoomExtension.registerExtension();
    MeasurementExtension.registerExtension();
    ToolHiderExtension.registerExtension();
    SettingsExtension.registerExtension();
    StatusExtension.registerExtension();
    AnalyticsExtension.registerExtension();
    PdmsAttrExtension.registerExtension();
    RoleExtension.registerExtension();
    MeasurementDataExtension.registerExtension();
    ShareExtension.registerExtension();
    NewCommentExtension.registerExtension();
    QuickFilterExtension.registerExtension();
    MarkupExtension.registerExtension();
    WebExtension.registerExtension();
    NodeExtension.registerExtension();
    PositionExtension.registerExtension();
    ExternalDataExtension.registerExtension();
    ToolTipExtension.registerExtension();
    GroupSelectionExtension.registerExtension();
    LegendPropertyExtension.registerExtension();
    BalconyGlassExtension.registerExtension();
    PdfNavExtension.registerExtension();
    CombiModelTreeExtension.registerExtension();
    BcfExtension.registerExtension();
}

function setupModelEvents(modelCount: number, loadedModels?: Array<SmartDrawings.SubModel>) {
    const onSdObjectTreeCreated = () => {
        // Temporary fix for Revit models
        // TODO: this might be possible to do in ViewerUtils.applyViewState
        initView();

        if (SettingsUtils.loadSingleSetting("hideGrid"))
            hideModuleGrid();

        hideNodes(viewerOptions.hiddenNodes);

        UiUtils.removeLoader();

        viewer.removeEventListener(SmartDrawings.SdEvents.SD_OBJECT_TREE_CREATED, onSdObjectTreeCreated);
    };

    viewer.addEventListener(SmartDrawings.SdEvents.SD_OBJECT_TREE_CREATED, onSdObjectTreeCreated);

    var modelsLoaded = 0;

    const allModelsInitialized = () => {
        modelsLoaded++;

        if (modelsLoaded !== modelCount) return;

        // Combi models: Make sure our model order matches viewer model order
        // Hide sub models with default visibility property set to false
        if (loadedModels != null) {
            for (let i = 0; i < loadedModels.length; i++) {
                let model = loadedModels[i];
                if (!model.defaultVisibility) {
                    ViewerUtils.setModelNodeVisibility(viewer.getAllModels()[i], false, viewer);
                }
            }

            viewerOptions.models = loadedModels;
        }

        viewer.removeEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, allModelsInitialized);
        viewer.dispatchEvent({ type: SmartDrawings.SdEvents.SD_OBJECT_TREE_CREATED });
    };

    // For loading single model with code (startLocalViewer)
    if (modelCount === 1 && loadedModels === undefined && viewer.model.isObjectTreeCreated())
        allModelsInitialized();
    else
        viewer.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, allModelsInitialized);
}

async function finalizeViewer(singleModel: boolean) {

    // The viewer preferences always have a true/false value for this setting even if the user hasn't touched it
    // so we need to check straight from local storage.
    try {
        const reverseZoom = localStorage.getItem("Autodesk.Viewing.Private.GuiViewer3D.SavedSettings.reverseMouseZoomDir");
        if (!reverseZoom) {
            viewer.setReverseZoomDirection(true);
        }
    } catch (e) {
        // Probably sandboxed, so no local storage access. Set always reversed.
        viewer.setReverseZoomDirection(true);
    }

    setVisualSettings();

    ViewerUtils.setLightingEnv(viewerOptions.viewParameters.lightingEnv,
        viewerOptions.viewParameters.lightingEnvBg, viewer);

    ViewerUtils.applyViewState(viewer, viewerOptions.viewParameters);

    registerBetterFirstPerson();

    UiUtils.setRevisionLink(viewerOptions);

    if (singleModel) {
        UiUtils.setDownloadLink(viewerOptions);
    }

    setGfxHelpers();
    UiUtils.setModelName(viewerOptions);
    initializeComments();
    initializeMeasurements();
    initializeFilters();
    initializeMarkup();
    initializeSection();

    initSearch(viewer);

    setTimeout(zoomToIsolatedOrSelected, 2000);

    //TODO: Check if this works with combo models (I don't think so)
    viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT,
        async ({ dbIdArray }) => {
            if (dbIdArray.length === 1) {
                const selectors = _.get(viewerState.currentRole, "selectionRules.selectors");
                if (!_.isEmpty(selectors)) {
                    const match = await ViewerUtils.findNodeMatchingProp(dbIdArray[0], selectors, viewer);
                    if (match != null && match !== dbIdArray[0]) {
                        viewer.select(match);
                        viewer.fitToView([match]);
                    }
                }
            }
        });

    const onExtensionLoaded = ({ extensionId }) => {
        if (extensionId === "Autodesk.Measure") {
            const ext = viewer.getExtension("Autodesk.Measure") as Autodesk.Extensions.Measure.MeasureExtension;

            if (viewerOptions.measurementCalibration != null) {
                const { unit, precision, scale } = viewerOptions.measurementCalibration;

                if (precision !== null) {
                    ext.sharedMeasureConfig.precision = precision;
                }
                if (unit != null && scale != null) {
                    ext.sharedMeasureConfig.units = unit;
                    ext.calibrateByScale(unit, scale);
                }
            }
            else {
                ext.setUnits(UnitConstants.DEFAULT_UNIT);
                ext.setPrecision(UnitConstants.DEFAULT_PRECISION);
            }

            viewer.removeEventListener(
                Autodesk.Viewing.EXTENSION_LOADED_EVENT,
                onExtensionLoaded);

        } else if (extensionId === "Autodesk.ViewCubeUi") {
            getViewCube().showTriad(true);
        } else if (extensionId === "Autodesk.PropertiesManager") {
            viewer.unregisterContextMenuCallback("propertiesmanager");
        } else if (extensionId === "Autodesk.ModelStructure") {
            // TODO: This will be deprecated and will be removed in v8.0.0 - Use extension "Autodesk.ModelStructure"
            (viewer as any).modelstructure.onTreeNodeClick = (tree, node, model: Autodesk.Viewing.Model, event) => {
                viewer.select(node, model);
                viewer.fitToView([node], model);
            }
        }
    }

    viewer.addEventListener(
        Autodesk.Viewing.EXTENSION_LOADED_EVENT,
        onExtensionLoaded);

    // Add sdApi for PowerBI etc.
    sdApi = new SdApi(viewer);
    window["sd"] = sdApi;
}

function getSdApi() {
    return sdApi;
}

async function initIdMap() {
    if (viewerOptions.tools.hasIdMap) {
        const { data } = await ModelApi.getIdMap(viewerOptions.guid || viewerOptions.models[0].guid);
        viewerOptions.idMap.addMap(1, viewerOptions.guid || viewerOptions.models[0].guid, data);
        (viewer as any).dispatchEvent({ type: SmartDrawings.SdEvents.ID_MAP_LOADED });
    }
}

async function initExternalData() {
    if (viewerOptions.externalDataFile != null) {
        const { data } = await ModelApi.getExternalData(viewerOptions.guid, viewerOptions.externalDataFile);
        viewerOptions.externalData = data;
        (viewer as any).dispatchEvent({ type: SmartDrawings.SdEvents.EXTERNAL_DATA_LOADED });

        const { idProperty } = data;
        // The data uses an alternate id (not guid) for element-specific data, so it should have an id map for that.
        if (idProperty != null) {
            const { data } = await ModelApi.getExternalIdMap(viewerOptions.guid || viewerOptions.models[0].guid);
            const paramsToDbIds = {};
            if (data != null) {
                for (let key in data) {
                    const value = data[key];
                    paramsToDbIds[value] = Number(key);
                }
            }

            viewerOptions.externalData.paramsToDbIds = paramsToDbIds;
            (viewer as any).dispatchEvent({ type: SmartDrawings.SdEvents.PARAMS_TO_DBIDS_MAPPED });
        }

    }
}

async function initWebData() {
    if (viewerOptions.webDataFile != null) {
        const { data } = await ModelApi.getModelFile(viewerOptions.guid, viewerOptions.webDataFile);
        viewerOptions.webData = data;
    }
}

async function initTourData() {
    if (viewerOptions.tourDataFile != null) {
        const { data } = await ModelApi.getTourData(viewerOptions.guid, viewerOptions.tourDataFile);
        viewerOptions.tourData = data;

        const { idProperty } = data;
        // The data uses an alternate id (not guid) for element-specific data, so it should have an id map for that.
        if (idProperty != null) {
            const { data } = await ModelApi.getExternalIdMap(viewerOptions.guid || viewerOptions.models[0].guid);
            const paramsToDbIds = {};
            if (data != null) {
                for (let key in data) {
                    const value = data[key];
                    paramsToDbIds[value] = Number(key);
                }
            }

            viewerOptions.tourData.paramsToDbIds = paramsToDbIds;
        }

        initMenu(viewer);
    }
}

function hideNodes(nodes: Array<SmartDrawings.ModelNode>) {
    const models = viewer.getAllModels();

    for (const node of nodes) {
        const model = models.find(mdl => mdl.id === node.modelId);

        (viewer.impl.visibilityManager as any).setNodeOff(node.nodeId, true, model);
        viewer.hide(node.nodeId, model);
    }
}

function hideModuleGrid() {
    const models = viewer.getAllModels();

    for (const model of models) {
        const instanceTree = model.getInstanceTree();

        for (let n = 0; n < instanceTree.nodeAccess.numNodes; n++) {
            if (instanceTree.getNodeName(n) != "Grid")
                continue;

            const hiddenNodes = viewerOptions.hiddenNodes as Array<SmartDrawings.ModelNode>;
            hiddenNodes.push(new SmartDrawings.ModelNode(model.id, n));
        }
    }
}

function registerBetterFirstPerson() {
    const fp = new FirstPerson(viewer);
    viewer.toolController.registerTool(fp);
    viewer.addEventListener(Autodesk.Viewing.NAVIGATION_MODE_CHANGED_EVENT, e => {
        if (e.id === "bimwalk") {
            viewer.toolController.activateTool(fp.getName());
        } else {
            viewer.toolController.deactivateTool(fp.getName());
        }
    });
}

async function zoomToIsolatedOrSelected() {

    if (await zoomAccordingToSearchParams()) {
        return;
    }

    const { isolatedNodes, selectedNodes, hiddenNodes, posVector } = viewerOptions.viewParameters;
    // The selected nodes may be Forge ids or IFC guids depending on the situation. 
    // Here we assume they are Forge ids if this is a URL copied from SD itself. (i.e. ViewParameters exist)
    const fromCopiedUrl = posVector != null;
    if (!_.isEmpty(isolatedNodes)) {
       
        if (fromCopiedUrl) {
            const aggregateSelection = viewerOptions.idMap.getAggregateFromShareSelections(isolatedNodes, viewer);
            aggregateSelection.map(s => viewer.isolate(s.ids, s.model));
        } else {
            const ids = getIds(viewerOptions.guid, isolatedNodes);
            viewer.isolate(ids);
            viewer.fitToView(ids);
        }
    } else if (!_.isEmpty(selectedNodes)) {
        if (fromCopiedUrl) {
            const aggregateSelection = viewerOptions.idMap.getAggregateFromShareSelections(selectedNodes, viewer);
            viewer.impl.selector.setAggregateSelection(aggregateSelection);
        } else {
            const ids = getIds(viewerOptions.guid, selectedNodes);
            viewer.select(ids);
            viewer.fitToView(ids);
        }
    }

    /**
     * If something is isolated, we assume hidden objects set is empty
     */
    if (!_.isEmpty(hiddenNodes)) {
        if (fromCopiedUrl) {
            const aggregateSelection = viewerOptions.idMap.getAggregateFromShareSelections(hiddenNodes, viewer);
            aggregateSelection.map(h => viewer.hide(h.ids, h.model));
        } else {
            const ids = getIds(viewerOptions.guid, hiddenNodes);
            viewer.hide(ids);
        }
    }
}

function getIds(modelGuid, nodes) {
    if (!viewerOptions.idMap.isEmpty()) {
        return viewerOptions.idMap.nodeIds(modelGuid, nodes);
    } else {
        return nodes.map(Number);
    }
}

/**
 * Attach DND handler for build order files and such.
 */
function attachDnd() {
    dnd = new DnDFileController("body",
        files => {
            const f = files[0];

            if (!f.type.match("application/json")) {
                alert("Not a valid JSON file.");
            }

            const reader = new FileReader();
            reader.onloadend = e => {
                const result = JSON.parse(reader.result as string);
                for (let l of dndListeners) {
                    const handled = l(f, result);
                    if (handled) {
                        break;
                    }
                }
            };
            reader.onerror = e => {
                alert("Failed to read the file.");
            };

            reader.readAsText(f);
        });
}

function setGfxHelpers() {
    helperGfx = new GfxUtils(viewer, "gfxHelpers");
    const coordGrid = SettingsUtils.loadSingleSetting("coordGrid");
    if (coordGrid) {
        if (!viewer.overlays.hasScene("gfxHelpers")) {
            viewer.overlays.addScene("gfxHelpers");
        }
        viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, () => helperGfx.update());
        helperGfx.drawAxis(viewer);
        helperGfx.update();
    }
}

function addDndListener(listener) {
    dndListeners.push(listener);
}

/**
 * If there are comments coming from share, initialize them in the comment extension.
 */
function initializeComments() {
    commentViewerUtils = new CommentViewerUtils(viewer,
        () => { },
        commentId => getCommentExtension().showComments(commentId));
    if (viewerOptions.comments != null) {
        viewerState.comments = {};
        for (let c of viewerOptions.comments) {
            viewerState.comments[c.id] = c;
            commentViewerUtils.addCommentMarker(c.id,
                new THREE.Vector3(c.position.x, c.position.y, c.position.z).toViewerUnits(),
                c.text, false);
        }
    }
}

function getCommentExtension(): NewCommentExtension {
    return viewer.getExtension("NewCommentExtension") as NewCommentExtension;
}

/**
 * If measurements in share, initialize them in the measure extension.
 */
function initializeMeasurements() {
    if (viewerOptions.measurements != null) {
        setTimeout(() => {
            ShareUtils.measurementsToExtension(viewer, viewerOptions.measurements);
            },
            2000);
    }
}

/*
 * If filters in share, initialize them in the filter extension.
 */
function initializeFilters() {
    if (viewerOptions.filters != null) {
        setTimeout(() => {
                ShareUtils.filtersToExtension(viewerOptions.filters);
            },
            2000);
    }
}

/*
 * If markups in share, initialize them in the markup extension.
 */
function initializeMarkup() {
    if (viewerOptions.markups != null) {
        setTimeout(() => {
                ShareUtils.markupToExtension(viewerOptions.markups);
            },
            2000);
    }
}

/*
 * If a section in share, initialize it in the section extension.
 */
function initializeSection() {
    if (viewerOptions.section != null) {
        setTimeout(() => {
                ShareUtils.sectionToExtension(viewer, viewerOptions.section);
            },
            2000);
    }
}

function setVisualShadowSettings() {
    const { userDefinedAttributes } = viewerOptions;

    const newRadius = userDefinedAttributes[SmartDrawings.UserDefinedAttributes.SHADOW_RADIUS];
    const newIntensity =
        userDefinedAttributes[SmartDrawings.UserDefinedAttributes.SHADOW_INTENSITY];

    if (newRadius == null && newIntensity == null)
        return;

    const renderer = (viewer.impl as any).renderer();

    const radius = newRadius ?? renderer.getAORadius();
    const intensity = newIntensity ?? renderer.getAOIntensity();

    renderer.setAOOptions(radius, intensity);
}

/**
 * Set visual settings defined in the model UDA.
 */
function setVisualSettings() {
    const { userDefinedAttributes } = viewerOptions;
    if (userDefinedAttributes == null) {
        return;
    }

    if (userDefinedAttributes[SmartDrawings.UserDefinedAttributes.FORGE_DISABLE_REFLECTION]) {
        (viewer.impl.matman() as any).setReflectionMap(null);
    }

    const forgeExp = userDefinedAttributes[SmartDrawings.UserDefinedAttributes.FORGE_EXPOSURE];
    if (forgeExp != null) {
        (viewer.impl.matman() as any).setEnvExposure(Number(forgeExp)); 
    }

    const forgeUp = userDefinedAttributes[SmartDrawings.UserDefinedAttributes.FORGE_UP_VECTOR];
    if (forgeUp != null) {
        const vector = new THREE.Vector3(0, 0, 0);
        vector[forgeUp] = 1;
        viewer.navigation.setWorldUpVector(vector, true, false);
        setTimeout(() => viewer.autocam.setCurrentViewAsHome(), 500);
    }

    setVisualShadowSettings();
}

function initView() {
    const { userDefinedAttributes } = viewerOptions;
    if (userDefinedAttributes == null) {
        return;
    }

    if (userDefinedAttributes["modelFix1"]) {
        console.log("UDA ModelFix1 found");
        var filter = { viewport: true };
        var state = viewer.getState(filter);
        viewer.fitToView();
        viewer.restoreState(state, filter, false);
    }
}

/**
 * Check for selection/isolation params in the URL and take them into effect.
 */
async function zoomAccordingToSearchParams() {
    const url = new URL(window.location.href);
    const selectionStr = url.searchParams.get(SmartDrawings.SdUrlParameters.SELECTION);
    const isolationStr = url.searchParams.get(SmartDrawings.SdUrlParameters.ISOLATION);
    if (_.isEmpty(selectionStr) && _.isEmpty(isolationStr)) {
        return false;
    }
    const paramsStr = selectionStr || isolationStr;

    if (!_.isEmpty(selectionStr)) {
        return await QuickFilterUtils.selectWithFilterString(paramsStr, viewer, true);
    } else {
        return await QuickFilterUtils.isolateWithFilterString(paramsStr, viewer, true);
    }

    return false;
}

/**
 * Make search according params in the URL
 */
async function searchAccordingToUrlParams() {
    if (hasUrlParam(SmartDrawings.SdUrlParameters.SEARCH)) {
        const searchValue = getUrlParamValue(SmartDrawings.SdUrlParameters.SEARCH);
        let op = "~";
        if (!_.isEmpty(searchValue)) {
            if (isUrlParamValueTrue(SmartDrawings.SdUrlParameters.EXACT_MATCH)) {
                op = "=";
            }
        }

        // Possible isolate and zoomfit
        const zoomFit = isUrlParamValueTrue(SmartDrawings.SdUrlParameters.SEARCH_ZOOMTOFIT);
        const isolate = isUrlParamValueTrue(SmartDrawings.SdUrlParameters.SEARCH_ISOLATE);

        const searchString = `*,*,${op},${searchValue}`;

        if (!isolate) {
            await QuickFilterUtils.selectWithFilterString(searchString, viewer, zoomFit);
        } else {
            await QuickFilterUtils.isolateWithFilterString(searchString, viewer, zoomFit);
        }

        // Update search bar
        $.when(checkSearchReady()).then(() => updateSearchBar(viewer));
    }
}

/**
 * Check url parameter exists
 * @param paramName Name of the query string parameter
 */
function hasUrlParam(paramName: string): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has(paramName);
}

/**
 * Get value of the url parameter
 * @param paramName Name of the query string parameter
 */
function getUrlParamValue(paramName: string): string {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(paramName);
}

/**
 * Check if url param value if true
 * If url param doesn't exist returns false
 * If it exists but has no value returns true
 */
function isUrlParamValueTrue(paramName: string): boolean {
    let isTrue = false;
    if (hasUrlParam(paramName)) {
        isTrue = true;
        const value = getUrlParamValue(paramName);
        if (!_.isEmpty(value)) {
            isTrue = value === "true";
        }
    }

    return isTrue;
}

function selectByGuid(guids, zoomToFit: boolean = false) {
    viewer.showAll();
    const dbIds = viewerOptions.idMap.nodeIds(null, guids);
    if (!_.isEqual(dbIds, viewer.getSelection().sort())) {
        viewer.select(dbIds);
        if (zoomToFit) {
            viewer.fitToView(dbIds);
        }
    }
}

function selectByDbId(ids) {
    viewer.select(ids);
}

function isolateByGuid(guids, zoomToFit: boolean = false) {
    viewer.clearSelection();
    const dbIds = viewerOptions.idMap.nodeIds(null, guids);
    if (!_.isEqual(dbIds, viewer.getIsolatedNodes().sort())) {
        viewer.isolate(dbIds);
        if (zoomToFit) {
            viewer.fitToView(dbIds);
        }
    }
}

function isolateByDbId(ids) {
    viewer.isolate(ids);
}

function setViewCube(orientation: string) {
    const cube = getViewCube();
    if (cube != null) {
        cube.setViewCube(orientation);
    } else {
        // The view cube is not available yet, wait for it.
        const onViewCubeLoaded = ({ extensionId }) => {
            if (extensionId === "Autodesk.ViewCubeUi") {
                getViewCube().setViewCube(orientation);
                viewer.removeEventListener(Autodesk.Viewing.EXTENSION_LOADED_EVENT, onViewCubeLoaded);
            }
        }
        viewer.addEventListener(Autodesk.Viewing.EXTENSION_LOADED_EVENT, onViewCubeLoaded);
    }
}

//TODO: Return type when Autodesk types includes extensions
function getViewCube(): any {
    return viewer.getExtension("Autodesk.ViewCubeUi");
}

function addSelectionListener(listener) {
    viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT,
        async ({ dbIdArray }) => {
            const guids = viewerOptions.idMap.guids(null, dbIdArray);
            listener({ dbIds: dbIdArray, guids: guids });
        });
}

function removeSelectionListener(listener) {
    viewer.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, listener);
}

function addSdModelLoadedListener(listener) {
    viewer.addEventListener(SmartDrawings.SdEvents.SD_OBJECT_TREE_CREATED, listener);
}

function removeSdModelLoadedListener(listener) {
    viewer.removeEventListener(SmartDrawings.SdEvents.SD_OBJECT_TREE_CREATED, listener);
}

export {
    startLocal,
    startCombo,
    viewerOptions,
    addDndListener,
    viewerState,
    commentViewerUtils,
    selectByGuid,
    selectByDbId,
    isolateByGuid,
    isolateByDbId,
    setViewCube,
    addSelectionListener,
    removeSelectionListener,
    addSdModelLoadedListener,
    removeSdModelLoadedListener,
    getSdApi
};
